const VFS_CDN_HOST = "img.vggcdn.net";

// Matches an existing `im=Resize`/`im=Crop` token, keeping the leading `?` or `&` so it can be replaced in place.
const VFS_IMAGE_OP_PATTERN = /([?&])im=(?:Resize|Crop)=\(\d+,\d+\)/;

const VFS_WEBP_PATTERN = /[?&]format=webp(?:&|$)/;

/**
 * Reports whether a source is served from the VFS CDN.
 */
export const isVfsImageSrc = (src: string): boolean => Boolean(src) && src.includes(VFS_CDN_HOST);

interface VfsResizeOptions {
  /** Append `format=webp` when the VFS-as-WebP experiment (VEP-1036) is in variant. */
  useWebp?: boolean;
}

/**
 * Applies a VFS CDN resize to a View From Seat image URL.
 *
 * The VFS CDN sizes images with the Akamai Image Manager token `im=Resize=(w,h)`. Curated fan images
 * are stored as raw VFS paths with no token, so the browser downloads the original asset. This
 * replaces an existing `im=Resize`/`im=Crop` token, or appends one when absent.
 *
 * Non-VFS URLs pass through unchanged.
 */
export const composeSrcWithVfsResizeParams = (
  src: string,
  width: number,
  height: number,
  options: VfsResizeOptions = {},
): string => {
  if (!isVfsImageSrc(src)) {
    return src;
  }

  const resizeParam = `im=Resize=(${width},${height})`;

  const resizedSrc = VFS_IMAGE_OP_PATTERN.test(src)
    ? src.replace(VFS_IMAGE_OP_PATTERN, `$1${resizeParam}`)
    : `${src}${src.includes("?") ? "&" : "?"}${resizeParam}`;

  if (options.useWebp && !VFS_WEBP_PATTERN.test(resizedSrc)) {
    return `${resizedSrc}${resizedSrc.includes("?") ? "&" : "?"}format=webp`;
  }

  return resizedSrc;
};
